import React from "react";
import { MessageCircle, MapPin, ArrowRight, ExternalLink, Store } from "lucide-react";
import { Ornament } from "@/components/Ornament";

export const Eyebrow = ({ children, dark = false }) => (
  <span className={`inline-block text-[11px] tracking-[0.4em] uppercase font-sans mb-4 ${dark ? "text-gold/80" : "text-gold-deep"}`}>
    {children}
  </span>
);

export const SectionTitle = ({ children, dark = false, className = "" }) => (
  <h2 className={`font-serif leading-[1.05] text-3xl sm:text-4xl lg:text-5xl ${dark ? "text-cream" : "text-espresso"} ${className}`}>
    {children}
  </h2>
);

// Shimmer sweep that runs on hover only.
const ShimmerWhite = () => (
  <span className="pointer-events-none absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out -skew-x-12 bg-gradient-to-r from-transparent via-white/55 to-transparent" />
);
const ShimmerGold = () => (
  <span className="pointer-events-none absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out -skew-x-12 bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
);

// Primary gold button — permanent glow, brighter shimmer on hover.
export const GoldButton = ({ as: As = "button", children, className = "", ...props }) => (
  <As
    className={`group relative inline-flex items-center gap-2.5 rounded-full bg-gold px-8 py-3.5 text-espresso-deep font-sans text-sm font-medium tracking-wide overflow-hidden shadow-[0_0_22px_rgba(200,169,126,0.55)] transition-all duration-300 hover:bg-gold-light hover:scale-[1.03] hover:shadow-[0_0_38px_rgba(200,169,126,0.85)] active:scale-95 ${className}`}
    {...props}
  >
    <ShimmerWhite />
    <span className="relative z-10 flex items-center gap-2.5">{children}</span>
  </As>
);

export const OutlineButton = ({ as: As = "button", children, dark = false, className = "", ...props }) => (
  <As
    className={`group relative inline-flex items-center gap-2.5 rounded-full border px-8 py-3.5 font-sans text-sm tracking-wide overflow-hidden transition-all duration-300 hover:scale-[1.03] active:scale-95 ${
      dark ? "border-gold/50 text-gold hover:bg-gold hover:text-espresso-deep hover:shadow-[0_0_26px_rgba(200,169,126,0.5)]" : "border-espresso/30 text-espresso hover:border-espresso hover:bg-espresso hover:text-cream hover:shadow-[0_0_24px_rgba(43,27,18,0.25)]"
    } ${className}`}
    {...props}
  >
    <ShimmerGold />
    <span className="relative z-10 flex items-center gap-2.5">{children}</span>
  </As>
);

// WhatsApp "reserve in store" — real WhatsApp green.
export const ReserveButton = ({ label, name, phone, className = "", testid = "reserve-button" }) => {
  const message = `Grüezi Mara-Team, ich möchte das Objekt ${name} gerne vor Ort in Basel anschauen.`;
  const href = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
  return (
    <a href={href} target="_blank" rel="noreferrer" data-testid={testid}
      className={`group relative inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-4 py-2.5 text-white font-sans text-xs font-medium tracking-wide overflow-hidden shadow-[0_0_16px_rgba(37,211,102,0.45)] transition-all duration-300 hover:bg-[#1ebe57] hover:scale-[1.03] hover:shadow-[0_0_26px_rgba(37,211,102,0.7)] active:scale-95 ${className}`}>
      <span className="pointer-events-none absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out -skew-x-12 bg-gradient-to-r from-transparent via-white/30 to-transparent" />
      <MessageCircle className="w-3.5 h-3.5 relative z-10" /> <span className="relative z-10">{label}</span>
    </a>
  );
};

// Marketplace "discover online" — Ricardo or Tutti.
export const MarketButton = ({ label, url, className = "", testid = "market-button" }) => (
  <a href={url} target="_blank" rel="noreferrer" data-testid={testid}
    className={`group relative inline-flex items-center justify-center gap-2 rounded-full border border-gold/50 px-4 py-2.5 text-gold font-sans text-xs tracking-wide overflow-hidden transition-all duration-300 hover:bg-gold hover:text-espresso-deep hover:scale-[1.03] hover:shadow-[0_0_22px_rgba(200,169,126,0.5)] active:scale-95 ${className}`}>
    <Store className="w-3.5 h-3.5" /> {label} <ExternalLink className="w-3 h-3" />
  </a>
);

export const WhatsAppButton = ({ label, message, phone, className = "", testid = "whatsapp-button" }) => {
  const href = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
  return (
    <a href={href} target="_blank" rel="noreferrer" data-testid={testid}
      className={`inline-flex items-center gap-2.5 rounded-full bg-[#25D366] px-6 py-3 text-white font-sans text-sm font-medium tracking-wide shadow-[0_0_18px_rgba(37,211,102,0.45)] transition-all duration-300 hover:bg-[#1ebe57] hover:scale-[1.03] hover:shadow-[0_0_30px_rgba(37,211,102,0.7)] active:scale-95 ${className}`}>
      <MessageCircle className="w-4 h-4" /> {label}
    </a>
  );
};

export const MapsButton = ({ label, query, dark = false, className = "", testid = "maps-button" }) => (
  <a href={`https://www.google.com/maps/dir/?api=1&destination=${query}`} target="_blank" rel="noreferrer" data-testid={testid}
    className={`inline-flex items-center gap-2.5 rounded-full px-6 py-3 font-sans text-sm tracking-wide transition-all duration-300 hover:scale-[1.03] active:scale-95 ${
      dark ? "border border-gold/50 text-gold hover:bg-gold hover:text-espresso-deep hover:shadow-[0_0_22px_rgba(200,169,126,0.5)]" : "border border-espresso/30 text-espresso hover:bg-espresso hover:text-cream hover:shadow-[0_0_22px_rgba(43,27,18,0.25)]"
    } ${className}`}>
    <MapPin className="w-4 h-4" /> {label}
  </a>
);

export const PageHero = ({ image, eyebrow, title, lead }) => (
  <section data-testid="page-hero" className="relative min-h-[62vh] flex items-end overflow-hidden">
    <div className="absolute inset-0 scale-110" style={{ backgroundImage: `url(${image})`, backgroundSize: "cover", backgroundPosition: "center" }} />
    <div className="absolute inset-0 bg-gradient-to-t from-espresso-deep via-espresso-deep/60 to-espresso-deep/30" />
    <div className="absolute inset-0 grain-overlay" />
    <div className="relative max-w-7xl mx-auto px-6 md:px-10 pb-16 pt-40 w-full">
      <Eyebrow dark>{eyebrow}</Eyebrow>
      <h1 className="font-serif text-cream text-4xl sm:text-5xl lg:text-6xl leading-[1.02] max-w-3xl text-shadow-warm">{title}</h1>
      {lead && <p className="mt-6 max-w-xl text-cream/80 text-base md:text-lg font-sans leading-relaxed">{lead}</p>}
      <Ornament className="mt-8 justify-start opacity-70" width={200} />
    </div>
  </section>
);

export { ArrowRight };
